import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import usePrefersReducedMotion from '../hooks/usePrefersReducedMotion';
import SmartiLabIcon from './SmartiLabIcon';
import styles from './SmartiLabRunOverlay.module.css';

const STEPS = [
  {
    phaseKeys: ['inspect_source_data', 'normalize_frames', 'aggregate_sessions'],
    it: 'Normalizzazione dati',
    en: 'Data normalization',
  },
  {
    phaseKeys: ['build_links'],
    it: 'Collegamento produzione e burn-in',
    en: 'Production and burn-in linking',
  },
  {
    phaseKeys: ['anomaly_risk_sequence_cluster', 'profile_data'],
    it: 'Ricerca anomalie',
    en: 'Anomaly detection',
  },
  {
    phaseKeys: ['random_forest_session'],
    it: 'Confronto con unità sane',
    en: 'Comparison with healthy units',
  },
  {
    phaseKeys: ['evidence_bundle_base', 'random_forest_candidate', 'evidence_bundle_final'],
    it: 'Calcolo punteggi di evidenza',
    en: 'Evidence scoring',
  },
  {
    phaseKeys: ['summarize_and_write', 'completed'],
    it: 'Preparazione risultati',
    en: 'Preparing results',
  },
];

const PULSES = [
  { x: '18%', y: '22%', delay: '0s' },
  { x: '76%', y: '18%', delay: '0.7s' },
  { x: '86%', y: '42%', delay: '1.3s' },
  { x: '72%', y: '78%', delay: '0.3s' },
  { x: '34%', y: '82%', delay: '0.9s' },
  { x: '14%', y: '58%', delay: '1.5s' },
];

function getCopy(stage, isItalian, activeStep, errorMessage) {
  const stepLabel = isItalian ? activeStep.it : activeStep.en;

  if (stage === 'success') {
    return {
      eyebrow: isItalian ? 'Run Smarti Lab completato' : 'Smarti Lab run completed',
      title: isItalian ? 'Motore ML aggiornato' : 'ML engine updated',
      subtitle: isItalian
        ? 'Le evidenze sono state rigenerate. Puoi continuare l’indagine con dati aggiornati.'
        : 'Evidence has been regenerated. You can continue the investigation with refreshed data.',
      footer: isItalian
        ? 'I risultati mostrano priorità di indagine, non cause confermate.'
        : 'Results show investigation priorities, not confirmed causes.',
      accent: 'success',
      stepLabel,
    };
  }

  if (stage === 'error') {
    return {
      eyebrow: isItalian ? 'Run Smarti Lab interrotto' : 'Smarti Lab run interrupted',
      title: isItalian ? 'Analisi non completata' : 'Analysis did not complete',
      subtitle: errorMessage || (isItalian
        ? 'Il motore ML non ha completato la ricostruzione. Controlla lo stato tecnico e riprova.'
        : 'The ML engine did not finish rebuilding. Check the technical status and try again.'),
      footer: isItalian
        ? 'I database sorgente non vengono modificati durante il run.'
        : 'Source databases are not modified during the run.',
      accent: 'error',
      stepLabel,
    };
  }

  if (stage === 'starting') {
    return {
      eyebrow: isItalian ? 'Avvio motore Smarti Lab' : 'Starting Smarti Lab engine',
      title: isItalian ? 'Preparazione run ML' : 'Preparing ML run',
      subtitle: isItalian
        ? 'Il sistema sta isolando il contesto di indagine e avviando la pipeline di ricostruzione.'
        : 'The system is isolating the investigation context and starting the rebuild pipeline.',
      footer: isItalian
        ? 'La schermata rimane attiva fino al completamento del run.'
        : 'This view stays active until the run completes.',
      accent: 'primary',
      stepLabel,
    };
  }

  return {
    eyebrow: isItalian ? 'Motore ML in esecuzione' : 'ML engine running',
    title: isItalian ? 'Analisi forense in corso' : 'Forensic analysis in progress',
    subtitle: isItalian
      ? 'Smarti Lab rilegge i database, ricostruisce i link burn-in e aggiorna evidenze, derive e sospetti.'
      : 'Smarti Lab is rereading the databases, rebuilding burn-in links, and refreshing evidence, drift, and suspects.',
    footer: isItalian
      ? 'Le evidenze vengono rigenerate senza modificare i dati sorgente.'
      : 'Evidence is regenerated without modifying source data.',
    accent: 'primary',
    stepLabel,
  };
}

export default function SmartiLabRunOverlay({
  open,
  stage = 'starting',
  errorMessage = '',
  errorTitle = '',
  errorDetails = '',
  recoverySteps = [],
  phaseKey = '',
  progressValue = null,
  onClose = null,
}) {
  const { i18n } = useTranslation();
  const prefersReducedMotion = usePrefersReducedMotion();
  const [elapsed, setElapsed] = useState(0);
  const isItalian = i18n.language !== 'en';

  useEffect(() => {
    if (!open) {
      setElapsed(0);
      return undefined;
    }

    if (prefersReducedMotion) {
      setElapsed(0);
      return undefined;
    }

    const started = Date.now();
    const timer = window.setInterval(() => {
      setElapsed(Date.now() - started);
    }, 120);

    return () => window.clearInterval(timer);
  }, [open, prefersReducedMotion]);

  const activeIndex = useMemo(() => {
    if (stage === 'success' || stage === 'error') return STEPS.length - 1;
    if (phaseKey) {
      const phaseMatch = STEPS.findIndex((step) => step.phaseKeys.includes(phaseKey));
      if (phaseMatch >= 0) return phaseMatch;
    }
    if (stage === 'starting') return 0;
    return Math.floor((elapsed / 1400) % STEPS.length);
  }, [elapsed, phaseKey, stage]);

  const activeStep = STEPS[activeIndex] || STEPS[0];
  const copy = getCopy(stage, isItalian, activeStep, errorMessage);
  const exactErrorText = String(errorDetails || errorMessage || '').trim();
  const shouldOfferDownload = exactErrorText.length > 280 || exactErrorText.includes('\n');
  const syntheticProgress = useMemo(() => {
    if (typeof progressValue === 'number' && Number.isFinite(progressValue)) {
      return Math.max(0.03, Math.min(1, progressValue / 100));
    }
    if (stage === 'success' || stage === 'error') return 1;
    if (stage === 'starting') return prefersReducedMotion ? 0.18 : 0.14 + ((elapsed % 1200) / 1200) * 0.16;
    if (prefersReducedMotion) return 0.72;
    return 0.28 + ((elapsed % 7600) / 7600) * 0.56;
  }, [elapsed, prefersReducedMotion, progressValue, stage]);
  const displayProgressPercent = Math.round(Math.max(8, syntheticProgress * 100));

  const activeCount = stage === 'success' || stage === 'error'
    ? STEPS.length
    : Math.max(1, activeIndex + 1);

  const handleDownloadError = () => {
    if (!exactErrorText) return;
    const payload = [
      errorTitle || 'Fusion pipeline failed',
      '',
      exactErrorText,
      recoverySteps.length ? `\nRecovery steps:\n- ${recoverySteps.join('\n- ')}` : '',
    ].join('\n');
    const blob = new Blob([payload], { type: 'text/plain;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'smarti-lab-fusion-error.txt';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(url);
  };

  if (!open) return null;

  return createPortal(
    <div
      className={`${styles.overlay} ${styles[`stage${stage.charAt(0).toUpperCase()}${stage.slice(1)}`]} ${prefersReducedMotion ? styles.reducedMotion : ''}`}
      role="status"
      aria-live="polite"
      aria-label={isItalian ? 'Analisi Smarti Lab in corso.' : 'Smarti Lab analysis in progress.'}
    >
      <div className={styles.grid} />
      <div className={styles.glow} />
      <div className={styles.scan} />

      <section className={styles.panel}>
        <div className={styles.halo} />
        <div className={styles.sideAura} />

        <div className={styles.shell}>
          <div className={styles.iconStage}>
            <div className={styles.ringOuter} />
            <div className={styles.ringInner} />
            <div className={styles.ringGhost} />
            {PULSES.map((pulse, index) => (
              <span
                key={`${pulse.x}-${pulse.y}-${index}`}
                className={styles.pulse}
                style={{ '--pulse-x': pulse.x, '--pulse-y': pulse.y, '--pulse-delay': pulse.delay }}
              />
            ))}
            <div className={styles.core}>
              <span className={styles.coreGlow} />
              <SmartiLabIcon className={styles.icon} />
            </div>
          </div>

          <div className={styles.copy}>
            <p className={styles.eyebrow}>{copy.eyebrow}</p>
            <h2 className={styles.title}>{copy.title}</h2>
            <p className={styles.subtitle}>{copy.subtitle}</p>

            {stage === 'error' && (errorTitle || errorDetails || recoverySteps.length) ? (
              <div className={styles.errorPanel}>
                {errorTitle ? <strong>{errorTitle}</strong> : null}
                {exactErrorText ? (
                  <div className={styles.errorDetailBlock}>
                    <span className={styles.errorDetailLabel}>{isItalian ? 'Errore esatto' : 'Exact error'}</span>
                    <pre className={styles.errorDetailText}>{exactErrorText}</pre>
                    {shouldOfferDownload ? (
                      <button type="button" className={styles.errorDownloadButton} onClick={handleDownloadError}>
                        {isItalian ? 'Scarica .txt' : 'Download .txt'}
                      </button>
                    ) : null}
                  </div>
                ) : null}
                {recoverySteps.length ? (
                  <ul>
                    {recoverySteps.map((step) => <li key={step}>{step}</li>)}
                  </ul>
                ) : null}
              </div>
            ) : null}

            <div className={styles.phasePill}>{copy.stepLabel}</div>

            <div className={styles.progressBlock}>
              <div className={styles.progressMeta}>
                <span>{isItalian ? 'Avanzamento run' : 'Run progress'}</span>
                <strong>{displayProgressPercent}%</strong>
              </div>
              <div className={styles.progressTrack}>
                <div
                  className={styles.progressFill}
                  style={{ '--progress': `${displayProgressPercent}%` }}
                />
              </div>
              <div className={styles.tickRow}>
                {STEPS.map((step, index) => (
                  <span
                    key={step.en}
                    className={`${styles.tick} ${index < activeCount ? styles.tickActive : ''}`}
                  />
                ))}
              </div>
            </div>

            <div className={styles.stepGrid}>
              {STEPS.map((step, index) => (
                <div
                  key={step.en}
                  className={`${styles.stepCard} ${index === activeIndex ? styles.stepCardActive : ''} ${index < activeCount ? styles.stepCardDone : ''}`}
                >
                  <span className={styles.stepIndex}>{String(index + 1).padStart(2, '0')}</span>
                  <strong>{isItalian ? step.it : step.en}</strong>
                </div>
              ))}
            </div>

            <p className={styles.footer}>{copy.footer}</p>
            {(stage === 'success' || stage === 'error') && onClose ? (
              <div className={styles.actions}>
                <button type="button" className={styles.closeButton} onClick={onClose}>
                  {isItalian ? 'Chiudi messaggio' : 'Close message'}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </section>
    </div>,
    document.body,
  );
}
